// src/components/Login.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../Services/supabaseClient";
import "./login.css";
import logo from "../components/Images/KostPadi-Icon.png";
import { useCompany } from "../context/CompanyContext";

const Login = ({ setIsLoggedIn, setUserProfile }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // grab our two setters from context (UNCHANGED)
  const { setCompanyId, setCompanyRole } = useCompany();

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setLoading(true);

    // 1) Sign in (UNCHANGED)
    const { data: loginData, error: loginError } =
      await supabase.auth.signInWithPassword({ email, password });

    if (loginError) {
      setErrorMessage("Invalid login credentials.");
      setLoading(false);
      return;
    }
    const user = loginData.user;
    if (!user) {
      setErrorMessage("Unexpected error: no user returned.");
      setLoading(false);
      return;
    }

    // 2) Load profile row (UNCHANGED)
    const { data: profileData, error: profileError } = await supabase
      .from("profiles")
      .select("first_name, last_name, id, has_onboarded, business_name")
      .eq("id", user.id)
      .single();

    if (profileError && profileError.code !== "PGRST116") {
      console.error("Profile fetch error:", profileError);
    }

    const { data: membership, error: membershipError } = await supabase
      .from("company_members")
      .select("company_id, role, business_role")
      .eq("user_id", user.id)
      .maybeSingle();

    if (membershipError) {
      console.error("Membership fetch error:", membershipError);
      setErrorMessage("Could not load your company access. Please try again.");
      return;
    }

    if (!membership?.company_id) {
      console.warn("No company membership found for user:", user.id);
      setErrorMessage(
        "Your account was created, but your company workspace is still being prepared. Please wait a few seconds and try logging in again."
      );

      await supabase.auth.signOut();
      localStorage.removeItem("isLoggedIn");
      setIsLoggedIn(false);
      return;
    }

    localStorage.setItem("isLoggedIn", "true");
    setIsLoggedIn(true);

    // Write into context (UNCHANGED)
    setCompanyId(membership.company_id);
    setCompanyRole(membership.role);

    // 4) Company info (UNCHANGED)
    const { data: compInfo, error: ciErr } = await supabase
      .from("company_info")
      .select("company_name, address, logo_url")
      .eq("company_id", membership.company_id)
      .single();
    if (ciErr && ciErr.code !== "PGRST116") {
      console.error("Company info fetch error:", ciErr);
      // proceed anyway
    }

    // 5) Set user profile (UNCHANGED)
    if (profileData) {
      setUserProfile(profileData);
    } else {
      setUserProfile({
        first_name: compInfo?.company_name || "Member",
        last_name: "",
        isInvited: true,
        companyInfo: compInfo,
      });
    }

    // 6) Mark logged in & go to dashboard (UNCHANGED)
    setIsLoggedIn(true);
    localStorage.setItem("isLoggedIn", "true");

    const businessRole = membership?.business_role;

    if (businessRole === "hr") {
      navigate("/payroll/dashboard");
      return;
    }

    if (businessRole === "marketing") {
      navigate("/crm");
      return;
    }

    navigate("/dashboard");
    window.location.reload();
    setLoading(false);
  };

  return (
    <div className="auth-layout">
      {/* Left: Brand / Value */}
      <aside className="auth-aside">
        <div className="brand-head">
          <img src={logo} alt="KostPadi" />
          <h1>KostPadi</h1>
        </div>
        <p className="tagline">
          A modern SME finance suite—estimate costs, invoice faster, and stay on
          top of cash flow.
        </p>

        <ul className="pill-list">
          <li>VAT/WHT-aware</li>
          <li>Estimate → Invoice</li>
          <li>Supabase RLS</li>
          <li>Export CSV/PDF</li>
        </ul>

        <div className="aside-footer">
          <a href="/" className="aside-link">
            ← Back to site
          </a>
        </div>
      </aside>

      {/* Right: Card Form */}
      <main className="auth-main">
        <div className="login-card">
          <header className="login-head">
            <img src={logo} alt="KostPadi Logo" className="logo-img" />
            <div>
              <h2>Welcome back</h2>
              <p className="muted">Sign in to your account</p>
            </div>
          </header>

          {errorMessage && (
            <div className="error-banner" role="alert">
              {errorMessage}
            </div>
          )}

          <form onSubmit={handleLogin} className="login-form">
            <div className="input-group-log">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                autoComplete="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
              />
            </div>

            <div className="input-group-log">
              <div className="label-row">
                <label htmlFor="password">Password</label>
                <a href="./ResetPassword" id="forgot-pass">
                  Forgot Password?
                </a>
              </div>

              <div className="pw-wrap">
                <input
                  id="password"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="pw-toggle"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  onClick={() => setShowPw((s) => !s)}
                >
                  {showPw ? "Hide" : "Show"}
                </button>
              </div>
            </div>

            <div className="row-between">
              <label className="remember">
                <input type="checkbox" /> <span>Remember me</span>
              </label>
            </div>

            <button type="submit" id="login-btn" disabled={loading}>
              {loading ? "Signing in…" : "Login"}
            </button>

            <p className="small">
              Don’t have an account?{" "}
              <a href="./SignUp" id="sign-up">
                Sign up
              </a>
            </p>
          </form>

          <div className="legal small">
            By continuing you agree to our <a href="/">Terms</a> and{" "}
            <a href="/">Privacy</a>.
          </div>
        </div>
      </main>
    </div>
  );
};

export default Login;
